import { Injectable } from '@angular/core';
import { RoleApiData, SheetKey } from '../../../types/api-response';
import { XBRL_PATTERNS, buildBackendKey } from './xbrl-transformation-patterns';
import { Dimension, Period, BackendDataObject } from './xbrl-transformation.types';

/**
 * Matches any key that already carries an XBRL-style namespace prefix
 * (e.g. `ifrs-full:`, `ifrs-smes:`, `ssmt-mfrs:`, `ssmt-mpers:`,
 * `ssmt-dei-ee-mfrs:`). Used by the per-role backend→form transformers to
 * avoid double-prefixing already-namespaced keys with `ifrs-full:`. The
 * earlier whitelist approach silently dropped `ifrs-smes:` qnames.
 */
const ALREADY_NAMESPACED = /^[a-z][a-z0-9-]*:/;

/**
 * Service to transform XBRL dimensional data between API format and form format
 *
 * API Format (simplified):
 * {
 *   "sepCur": "value",    // Separate Consolidated Current Year
 *   "sepPrev": "value",   // Separate Consolidated Previous Year
 *   "conCur": "value",    // Consolidated Current Year
 *   "conPrev": "value"    // Consolidated Previous Year
 * }
 *
 * Form Format (composite keys - simplified):
 * {
 *   "qname[Consolidated][CurrentYear]": "value",
 *   "qname[Consolidated][PreviousYear]": "value",
 *   "qname[Separate][CurrentYear]": "value",
 *   "qname[Separate][PreviousYear]": "value"
 * }
 */
@Injectable({
  providedIn: 'root'
})
export class XbrlDataTransformerService {

  /**
   * Parse numeric values from API, PRESERVING NULL values
   *
   * This method correctly distinguishes between:
   * - null/undefined/empty string → null (represents "no data" - empty input field)
   * - "0" (string) → 0 (number) (represents actual zero value)
   * - "123.45" (string) → 123.45 (number)
   * - 0 (number) → 0 (number)
   *
   * Background:
   * The backend API sends explicit null when a field has no data. Converting null to 0
   * loses this distinction and causes forms to display "0" instead of empty fields.
   * This is critical for financial reporting accuracy.
   *
   * @param value - Value from backend API (string, number, null, undefined)
   * @returns Parsed number or null for missing/invalid values
   *
   * @example
   * parseNumericValue(null)      // Returns: null (empty input field)
   * parseNumericValue(undefined) // Returns: null (empty input field)
   * parseNumericValue("")        // Returns: null (empty input field)
   * parseNumericValue("0")       // Returns: 0 (actual zero)
   * parseNumericValue(0)         // Returns: 0 (actual zero)
   * parseNumericValue("123.45")  // Returns: 123.45
   * parseNumericValue("invalid") // Returns: null (invalid input)
   */
  private parseNumericValue(value: any): number | null {
    // Handle null, undefined, or empty string as "no data"
    if (value === null || value === undefined || value === '') {
      return null;
    }

    // If already a number, return it (including 0)
    if (typeof value === 'number') {
      return value;
    }

    // Convert string to number
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? null : parsed;
    }

    // Unexpected type - return null
    return null;
  }

  /**
   * The four dual-column cell keys a dimensional slice stores its
   * (dimension × period) values under, in canonical camelCase. The single source
   * of truth for {@link readDualColumnCell}'s case-insensitive matching.
   */
  private static readonly DUAL_COLUMN_CELLS = [
    'conCur',
    'conPrev',
    'sepCur',
    'sepPrev',
  ] as const;

  /**
   * Read a dual-column nested cell (`conCur` / `conPrev` / `sepCur` / `sepPrev`)
   * **case-insensitively**, from ONE place, so the bracket-format transforms never
   * re-hardcode a per-cell casing fallback.
   *
   * Why case-insensitive: the standard (N8N) flow ships lowercase keys, but the
   * v2 direct-LLM export ships PascalCase (`ConCur` …). Rather than pin the two
   * known spellings, match on a lowercased key so ANY casing drift (`CONCUR`,
   * `ConCur`, `concur`) resolves. Without this a mismatched-casing slice silently
   * transforms to `{}` and every dual-column lookup on the sheet misses (empty
   * required fields / cross-sheet operands → false validation errors).
   *
   * Exact hits (the two shipped spellings) short-circuit before the scan, so the
   * hot path stays O(1); the fallback scan is bounded by the ~4 keys a cell object
   * holds. Returns `undefined` when the cell is absent under any casing.
   */
  private readDualColumnCell(
    data: Record<string, any> | null | undefined,
    cell: (typeof XbrlDataTransformerService.DUAL_COLUMN_CELLS)[number]
  ): any {
    if (!data)
        return undefined;
    
    // Fast path: the lowercase (standard) then PascalCase (v2 direct) spellings.
    if (data[cell] !== undefined)
      return data[cell];
    
    const pascal = cell.charAt(0).toUpperCase() + cell.slice(1);
    if (data[pascal] !== undefined)
      return data[pascal];
    
    // Fallback: resolve any other casing via a single lowercased scan.
    const target = cell.toLowerCase();
    for (const key of Object.keys(data)) {
      if (key.toLowerCase() === target)
        return data[key];
    }
    return undefined;
  }

  /**
   * Transform API response (simplified format) to form format (composite keys)
   * @param qname The XBRL concept name
   * @param apiData The API response with keys: sepCur, sepPrev, conCur, conPrev
   * @param prefix XBRL taxonomy prefix (ifrs-full for MFRS, ifrs-smes for MPERS)
   * @returns Form data with composite keys (values converted to numbers)
   *
   * Casing-tolerant via {@link readDualColumnCell} (accepts lowercase `sepCur` or
   * PascalCase `SepCur`, or any casing) — the standard flow ships lowercase, the
   * v2 direct-LLM export ships PascalCase.
   */
  transformApiToFormFormat(
    qname: string,
    apiData: Record<string, any> | BackendDataObject,
    prefix: 'ifrs-full' | 'ifrs-smes' = 'ifrs-full'
  ): Record<string, number | null> {
    const formData: Record<string, any> = {};
    const data = apiData as Record<string, any>;

    // Map API keys to form composite keys with numeric conversion
    // Parse first, then only add if not null (handles empty strings correctly)
    const conCurValue = this.parseNumericValue(this.readDualColumnCell(data, 'conCur'));
    if (conCurValue !== null) {
      formData[`${qname}[Consolidated][CurrentYear]`] = conCurValue;
    }

    const conPrevValue = this.parseNumericValue(this.readDualColumnCell(data, 'conPrev'));
    if (conPrevValue !== null) {
      formData[`${qname}[Consolidated][PreviousYear]`] = conPrevValue;
    }

    const sepCurValue = this.parseNumericValue(this.readDualColumnCell(data, 'sepCur'));
    if (sepCurValue !== null) {
      formData[`${qname}[Separate][CurrentYear]`] = sepCurValue;
    }

    const sepPrevValue = this.parseNumericValue(this.readDualColumnCell(data, 'sepPrev'));
    if (sepPrevValue !== null) {
      formData[`${qname}[Separate][PreviousYear]`] = sepPrevValue;
    }

    return formData;
  }

  /**
   * Transform form format back to API format (for sending to backend)
   * @param qname The XBRL concept name
   * @param formData Form data with composite keys
   * @param prefix XBRL taxonomy prefix (kept for backward compatibility but not used in new format)
   * @returns API format with keys: sepCur, sepPrev, conCur, conPrev
   */
  transformFormToApiFormat(
    qname: string,
    formData: Record<string, any>,
    prefix: 'ifrs-full' | 'ifrs-smes' = 'ifrs-full'
  ): BackendDataObject {
    // Initialize with all four dimensional keys set to null
    // Backend expects complete object even if some values are null
    const apiData: BackendDataObject = {
      conCur: null, conPrev: null,
      sepCur: null, sepPrev: null,
    };

    // Define the composite keys we're looking for (new simplified format)
    const conCurKey = `${qname}[Consolidated][CurrentYear]`;
    const conPrevKey = `${qname}[Consolidated][PreviousYear]`;
    const sepCurKey = `${qname}[Separate][CurrentYear]`;
    const sepPrevKey = `${qname}[Separate][PreviousYear]`;

    // Map form keys back to API format (only overwrite if value exists)
    if (formData[conCurKey] !== undefined && formData[conCurKey] !== null) {
      apiData['conCur'] = formData[conCurKey];
    }

    if (formData[conPrevKey] !== undefined && formData[conPrevKey] !== null) {
      apiData['conPrev'] = formData[conPrevKey];
    }

    if (formData[sepCurKey] !== undefined && formData[sepCurKey] !== null) {
      apiData['sepCur'] = formData[sepCurKey];
    }

    if (formData[sepPrevKey] !== undefined && formData[sepPrevKey] !== null) {
      apiData['sepPrev'] = formData[sepPrevKey];
    }

    return apiData;
  }

  /**
   * Transform a collection of line items from API format to form format
   * @param lineItems Array of items with qname and dimensional data
   * @param prefix XBRL taxonomy prefix
   * @returns Merged form data for all line items
   */
  transformLineItemsToForm(
    lineItems: Array<{ qname: string; [key: string]: any }>,
    prefix: 'ifrs-full' | 'ifrs-smes' = 'ifrs-full'
  ): Record<string, any> {
    const allFormData: Record<string, any> = {};

    lineItems.forEach(item => {
      const qname = item.qname;
      // Use destructuring to extract qname and separate it from the rest of the data
      const { qname: _, ...itemData } = item;

      const transformedData = this.transformApiToFormFormat(qname, itemData, prefix);
      Object.assign(allFormData, transformedData);
    });

    return allFormData;
  }

  /**
   * Transform API response to bracket format (for Roles 12-18)
   * PHASE 2: Updated to use simplified bracket notation for consistency
   *
   * Used by components in Roles 12-18 (SOPL, SOCI, Cash Flows)
   * OLD FORMAT: "qnameConsolidatedCurrent"
   * NEW FORMAT: "qname[Consolidated][CurrentYear]"
   *
   * @param qname The XBRL concept name
   * @param apiData The API response with keys: sepCur, sepPrev, conCur, conPrev
   * @returns Form data with bracket notation keys (values converted to numbers)
   *
   * Casing-tolerant (roles 12-18) via {@link readDualColumnCell}: the standard
   * flow ships lowercase dual-column keys, the v2 direct-LLM export ships
   * PascalCase. Without this a mismatched-casing slice transforms to `{}` and
   * every dual-column lookup on roles 12-18 misses (empty required fields /
   * cross-sheet operands → false errors).
   */
  transformApiToSimpleFormat(
    qname: string,
    apiData: Record<string, any>
  ): Record<string, any> {
    const formData: Record<string, any> = {};

    // Map API keys to bracket format with numeric conversion (PHASE 2: simplified notation)
    // Parse first, then only add if not null (handles empty strings correctly)
    const conCurValue = this.parseNumericValue(this.readDualColumnCell(apiData, 'conCur'));
    if (conCurValue !== null) {
      formData[`${qname}[Consolidated][CurrentYear]`] = conCurValue;
    }

    const conPrevValue = this.parseNumericValue(this.readDualColumnCell(apiData, 'conPrev'));
    if (conPrevValue !== null) {
      formData[`${qname}[Consolidated][PreviousYear]`] = conPrevValue;
    }

    const sepCurValue = this.parseNumericValue(this.readDualColumnCell(apiData, 'sepCur'));
    if (sepCurValue !== null) {
      formData[`${qname}[Separate][CurrentYear]`] = sepCurValue;
    }

    const sepPrevValue = this.parseNumericValue(this.readDualColumnCell(apiData, 'sepPrev'));
    if (sepPrevValue !== null) {
      formData[`${qname}[Separate][PreviousYear]`] = sepPrevValue;
    }

    return formData;
  }

  /**
   * Transform a collection of line items to simple format (for Role12, Role14)
   * @param apiData Object with qname keys and dimensional data values
   * @returns Merged form data for all line items with simple concatenated keys
   */
  transformApiDataToSimpleFormat(
    apiData: Record<string, any>
  ): Record<string, any> {
    const allFormData: Record<string, any> = {};

    Object.entries(apiData).forEach(([qname, dimensionalData]) => {
      if (dimensionalData && typeof dimensionalData === 'object') {
        const transformedData = this.transformApiToSimpleFormat(qname, dimensionalData);
        Object.assign(allFormData, transformedData);
      }
    });

    return allFormData;
  }

  /**
   * Transform API dimensional data to bracket format with member dimensions (Phase 3)
   * Used by Statement of Changes in Equity (Role 19)
   *
   * API Format:
   * {
   *   "ifrs-full:ChangesInEquity:IssuedCapitalMember": { "sepCur": "0", "sepPrev": "0", "conCur": "0", "conPrev": "0" },
   *   "ifrs-full:ChangesInEquity": { "sepCur": "0", "sepPrev": "0", "conCur": "0", "conPrev": "0" },
   *   "ifrs-full:Equity:Beginning:EquityAttributableToOwnersOfParentMember": { "ConCur": "18992436", ... }
   * }
   *
   * Form Format (STANDARD BRACKET FORMAT - Phase 3):
   * {
   *   "ifrs-full:ChangesInEquity:IssuedCapitalMember[Consolidated][CurrentYear]": 0,
   *   "ifrs-full:ChangesInEquity:IssuedCapitalMember[Separate][CurrentYear]": 0,
   *   "ifrs-full:ChangesInEquity:EquityMember[Consolidated][CurrentYear]": 0 (default member),
   *   "ifrs-full:EquityAtBeginningOfPeriod:EquityAttributableToOwnersOfParentMember[Consolidated][CurrentYear]": 18992436
   * }
   *
   * A key with no member segment gets the EquityMember of its OWN taxonomy
   * (ifrs-full for MFRS, ifrs-smes for MPERS), derived from the key prefix.
   *
   * @param apiData Object with colon-separated keys (concept:member or concept) and dimensional data values
   * @returns Merged form data with bracket-formatted keys (values converted to numbers)
   */
  transformApiDimensionalDataToUnderscoreFormat(
    apiData: Record<string, any>
  ): Record<string, any> {
    const allFormData: Record<string, any> = {};

    Object.entries(apiData).forEach(([apiKey, dimensionalData]) => {
      if (!dimensionalData || typeof dimensionalData !== 'object') {
        return;
      }

      // Split the API key by colon to get concept and member
      const segments = apiKey.split(':');

      let concept: string;
      let member: string;

      if (segments.length === 4) {
        // Format: "prefix:baseConcept:periodQualifier:member"
        // Example: "ssmt-mfrs:Equity:Beginning:EquityAttributableToOwnersOfParentMember"
        // Maps to: "ifrs-full:Equity:Beginning:ifrs-full:EquityAttributableToOwnersOfParentMember"
        const prefix = segments[0]; // "ssmt-mfrs" or "ssmt-mpers" or "ifrs-full"
        const baseConcept = segments[1]; // "Equity"
        const periodQualifier = segments[2]; // "Beginning" or "End"
        const memberName = segments[3]; // "EquityAttributableToOwnersOfParentMember"

        // IMPORTANT: Convert concept prefix to match schema's row qnames
        // The schema uses ifrs-full: or ifrs-smes: prefix for Beginning/End period items
        // - MFRS (ssmt-mfrs: or ifrs-full:) → concepts use ifrs-full: prefix
        // - MPERS (ssmt-mpers: or ifrs-smes:) → concepts use ifrs-smes: prefix
        const conceptPrefix = prefix.includes('mpers') || prefix.includes('smes') ? 'ifrs-smes' : 'ifrs-full';
        concept = `${conceptPrefix}:${baseConcept}:${periodQualifier}`; // "ifrs-full:Equity:Beginning"

        // IMPORTANT: Use the correct prefix for member names to match schema's equityColumns
        // - MFRS (ssmt-mfrs: or ifrs-full:) → members use ifrs-full: prefix
        // - MPERS (ssmt-mpers: or ifrs-smes:) → members use ifrs-smes: prefix
        const memberPrefix = prefix.includes('mpers') || prefix.includes('smes') ? 'ifrs-smes' : 'ifrs-full';
        member = `${memberPrefix}:${memberName}`; // "ifrs-full:EquityAttributableToOwnersOfParentMember"

      } else if (segments.length === 3) {
        // Two shapes share the 3-segment count:
        //   (a) "prefix:concept:member"             — e.g. "ssmt-mfrs:EquityBalanceRestated:IssuedCapitalMember"
        //   (b) "prefix:Equity:Beginning|End"       — memberless period-qualified total (the
        //       Total column of the "Equity at beginning/end of period" rows). The backend
        //       persists those cells without a member suffix (per MfrsSocieDto/MpersSocieDto
        //       JsonPropertyName attributes), so they reach us as 3 segments where the third
        //       segment is `Beginning`/`End` — a period qualifier, NOT a member.
        const prefix = segments[0]; // "ssmt-mfrs" or "ssmt-mpers" or "ifrs-full" or "ifrs-smes"
        const conceptName = segments[1]; // "EquityBalanceRestated" or "Equity"
        const memberName = segments[2]; // "IssuedCapitalMember" or "Beginning"/"End"

        const isMPERS = prefix.includes('mpers') || prefix.includes('smes');

        if (conceptName === 'Equity' && (memberName === 'Beginning' || memberName === 'End')) {
          // Shape (b): memberless period-qualified equity total. Inject the default
          // EquityMember so the form key matches role-19's control name produced by
          // getEquityColumnControlName(rowQname='ifrs-full:Equity:Beginning|End',
          // columnQname='ifrs-full:EquityMember', ...). Mirrors the outbound
          // EquityMember-strip in transformUnderscoreFormatToApiDimensional so the
          // round trip is symmetric.
          const conceptPrefix = isMPERS ? 'ifrs-smes' : 'ifrs-full';
          concept = `${conceptPrefix}:Equity:${memberName}`;
          member = isMPERS ? 'ifrs-smes:EquityMember' : 'ifrs-full:EquityMember';
        } else {
          // Shape (a): prefix:concept:member.
          // IMPORTANT: Determine the correct concept prefix based on the schema.
          // Some concepts use ssmt-* prefix in the schema, others use ifrs-* prefix.
          // - MFRS ssmt-* concepts: ImpactOfChangeInAccountingStandards, EquityBalanceRestated
          // - MPERS ssmt-* concepts: ImpactOfChangeInAccountingStandards, EquityBalanceRestated,
          //   IncreaseDecreaseThroughAcquisitionOfSubsidiary, IncreaseDecreaseThroughConversionOfConvertibleInstruments,
          //   IssueOfConvertibleInstruments, IncreaseDecreaseThroughTransactionsWithOwners
          const ssmtMfrsOnlyConcepts = ['ImpactOfChangeInAccountingStandards', 'EquityBalanceRestated'];
          const ssmtMpersOnlyConcepts = [
            'ImpactOfChangeInAccountingStandards',
            'EquityBalanceRestated',
            'IncreaseDecreaseThroughAcquisitionOfSubsidiary',
            'IncreaseDecreaseThroughConversionOfConvertibleInstruments',
            'IssueOfConvertibleInstruments',
            'IncreaseDecreaseThroughTransactionsWithOwners'
          ];

          const ssmtConcepts = isMPERS ? ssmtMpersOnlyConcepts : ssmtMfrsOnlyConcepts;

          // If the concept is in the ssmt-* list, keep the ssmt-* prefix.
          // Otherwise, convert to ifrs-* prefix to match the schema.
          if (ssmtConcepts.includes(conceptName)) {
            concept = `${prefix}:${conceptName}`;
          } else {
            // - MFRS → ifrs-full:, MPERS → ifrs-smes:
            const conceptPrefix = isMPERS ? 'ifrs-smes' : 'ifrs-full';
            concept = `${conceptPrefix}:${conceptName}`;
          }

          // IMPORTANT: Use the correct prefix for member names to match schema's equityColumns.
          // - MFRS → ifrs-full:, MPERS → ifrs-smes:
          const memberPrefix = isMPERS ? 'ifrs-smes' : 'ifrs-full';
          member = `${memberPrefix}:${memberName}`; // "ifrs-full:IssuedCapitalMember" or "ifrs-smes:IssuedCapitalMember"
        }

      } else if (segments.length === 2) {
        // Format: "prefix:concept" (no member - use default)
        // Example: "ifrs-full:ChangesInEquity" or "ifrs-smes:ChangesInEquity"
        concept = apiKey; // "ifrs-full:ChangesInEquity" or "ifrs-smes:ChangesInEquity"

        // IMPORTANT: Use the correct default member based on the API key's prefix
        // - MFRS (ifrs-full:) → use ifrs-full:EquityMember
        // - MPERS (ifrs-smes: or ssmt-mpers:) → use ifrs-smes:EquityMember
        const prefix = segments[0];
        const isMPERS = prefix.includes('mpers') || prefix.includes('smes');
        member = isMPERS ? 'ifrs-smes:EquityMember' : 'ifrs-full:EquityMember';
      } else {
        // Unexpected format - skip
        return;
      }

      // Transform dimensional data to STANDARD BRACKET FORMAT (Phase 3)
      // Format: concept:member[Consolidated|Separate][CurrentYear|PreviousYear]
      // Casing tolerated via readDualColumnCell (lowercase / PascalCase / any).
      // Parse first, then only add if not null (handles empty strings correctly)
      const conCurValue = this.parseNumericValue(this.readDualColumnCell(dimensionalData, 'conCur'));
      if (conCurValue !== null) {
        const key = `${concept}:${member}[Consolidated][CurrentYear]`;
        allFormData[key] = conCurValue;
      }

      const conPrevValue = this.parseNumericValue(this.readDualColumnCell(dimensionalData, 'conPrev'));
      if (conPrevValue !== null) {
        const key = `${concept}:${member}[Consolidated][PreviousYear]`;
        allFormData[key] = conPrevValue;
      }

      const sepCurValue = this.parseNumericValue(this.readDualColumnCell(dimensionalData, 'sepCur'));
      if (sepCurValue !== null) {
        const key = `${concept}:${member}[Separate][CurrentYear]`;
        allFormData[key] = sepCurValue;
      }

      const sepPrevValue = this.parseNumericValue(this.readDualColumnCell(dimensionalData, 'sepPrev'));
      if (sepPrevValue !== null) {
        const key = `${concept}:${member}[Separate][PreviousYear]`;
        allFormData[key] = sepPrevValue;
      }
    });

    return allFormData;
  }

  /**
   * Transform bracket format back to API dimensional format (Phase 3)
   * Reverses the transformation done by transformApiDimensionalDataToUnderscoreFormat
   *
   * Input format: concept:member[Consolidated|Separate][CurrentYear|PreviousYear]
   * Output format: API colon-separated keys with dimensional data values
   *
   * @param formData Object with bracket-formatted keys
   * @returns API format with colon-separated keys and dimensional data values
   */
  transformUnderscoreFormatToApiDimensional(
    formData: Record<string, any>
  ): Record<string, any> {
    const apiData: Record<string, any> = {};

    Object.entries(formData).forEach(([formKey, value]) => {
      // Parse bracket format: "concept:member[dimension][period]"
      // New format: "concept:member[dimension][period]" where member includes prefix
      // Example: "ifrs-full:ChangesInEquity:ifrs-full:IssuedCapitalMember[Consolidated][CurrentYear]"
      // Example: "ifrs-full:Equity:Beginning:ifrs-full:IssuedCapitalMember[Consolidated][CurrentYear]"

      // Split at bracket to separate the key part from dimension/period
      const bracketIndex = formKey.indexOf('[');
      if (bracketIndex === -1) {
        return; // No bracket found - skip
      }

      const keyPart = formKey.substring(0, bracketIndex);
      const dimensionPart = formKey.substring(bracketIndex);

      // Parse dimension and period from bracket part: "[Consolidated][CurrentYear]"
      const dimensionMatch = dimensionPart.match(/^\[(.+?)\]\[(.+?)\]$/);
      if (!dimensionMatch) {
        return; // Invalid bracket format - skip
      }

      const dimension = dimensionMatch[1]; // "Consolidated" or "Separate"
      const period = dimensionMatch[2]; // "CurrentYear" or "PreviousYear"

      // Parse the key part to extract concept and member
      // Key can be:
      // - "prefix:concept:prefix:member" (3-segment concept)
      // - "prefix:concept:qualifier:prefix:member" (4-segment concept with Beginning/End)
      const keySegments = keyPart.split(':');

      let apiKey: string;
      let isEquityPeriodKey = false;

      if (keySegments.length === 5) {
        // Format: "prefix:concept:qualifier:memberPrefix:memberName"
        // Example: "ifrs-full:Equity:Beginning:ifrs-full:IssuedCapitalMember"
        const prefix = keySegments[0];
        const baseConcept = keySegments[1];
        const qualifier = keySegments[2]; // "Beginning" or "End"
        const memberName = keySegments[4]; // "IssuedCapitalMember"

        // The Total column is stored memberless on the backend — e.g. the
        // "Equity at beginning of period" total cell is "ifrs-full:Equity:Beginning",
        // not "ifrs-full:Equity:Beginning:EquityMember". The form represents that
        // Total column as the default EquityMember member, so strip it here so the
        // cell round-trips to the persisted key (same rule as the 4-segment branch).
        apiKey = memberName === 'EquityMember'
          ? `${prefix}:${baseConcept}:${qualifier}`
          : `${prefix}:${baseConcept}:${qualifier}:${memberName}`;
        isEquityPeriodKey = qualifier === 'Beginning' || qualifier === 'End';
      } else if (keySegments.length === 4) {
        // Format: "prefix:concept:memberPrefix:memberName"
        // Example: "ifrs-full:ChangesInEquity:ifrs-full:IssuedCapitalMember"
        const prefix = keySegments[0];
        const conceptName = keySegments[1];
        const memberName = keySegments[3]; // "IssuedCapitalMember"

        // The default member column (ifrs-full:EquityMember / ifrs-smes:EquityMember)
        // is stored memberless on the backend. transformApiDimensionalDataToUnderscoreFormat
        // injects EquityMember when it reads a memberless API key, so the reverse must
        // strip it again — otherwise the column round-trips as ".../...:EquityMember"
        // and no longer matches the persisted key.
        apiKey = memberName === 'EquityMember'
          ? `${prefix}:${conceptName}`
          : `${prefix}:${conceptName}:${memberName}`;
      } else {
        // Unexpected format - skip
        return;
      }

      // Initialize dimensional data object if it doesn't exist
      // Initialize with all four keys set to null (backend expects complete object)
      if (!apiData[apiKey]) {
        if (isEquityPeriodKey) {
          apiData[apiKey] = {
            ConCur: null,
            ConPrev: null,
            SepCur: null,
            SepPrev: null,
          };
        } else {
          apiData[apiKey] = {
            conCur: null,
            conPrev: null,
            sepCur: null,
            sepPrev: null,
          };
        }
      }

      // Map dimension and period to API format key
      // Use uppercase for 4-segment keys (Beginning/End), lowercase for 3-segment keys
      let dimensionalKey: string;

      if (dimension === 'Consolidated' && period === 'CurrentYear') {
        dimensionalKey = isEquityPeriodKey ? 'ConCur' : 'conCur';
      } else if (dimension === 'Consolidated' && period === 'PreviousYear') {
        dimensionalKey = isEquityPeriodKey ? 'ConPrev' : 'conPrev';
      } else if (dimension === 'Separate' && period === 'CurrentYear') {
        dimensionalKey = isEquityPeriodKey ? 'SepCur' : 'sepCur';
      } else if (dimension === 'Separate' && period === 'PreviousYear') {
        dimensionalKey = isEquityPeriodKey ? 'SepPrev' : 'sepPrev';
      } else {
        return; // Invalid dimension/period combination
      }

      apiData[apiKey][dimensionalKey] = value;
    });

    return apiData;
  }

  /**
   * Transform backend business member format to frontend bracket format
   * Used for MSICCode and DescriptionOfBusiness in Role-2 (Scope of Filing)
   *
   * Backend Format:
   * {
   *   "ssmt:MSICCode": {
   *     "businessOneMember": "70209",
   *     "businessTwoMember": "64200",
   *     "businessThreeMember": ""
   *   },
   *   "ssmt:DescriptionOfBusiness": {
   *     "businessOneMember": "Description 1",
   *     "businessTwoMember": "Description 2",
   *     "businessThreeMember": ""
   *   }
   * }
   *
   * Frontend Format:
   * {
   *   "ssmt:MSICCode[ssmt:BusinessOneMember]": "70209",
   *   "ssmt:MSICCode[ssmt:BusinessTwoMember]": "64200",
   *   "ssmt:MSICCode[ssmt:BusinessThreeMember]": "",
   *   "ssmt:DescriptionOfBusiness[ssmt:BusinessOneMember]": "Description 1",
   *   "ssmt:DescriptionOfBusiness[ssmt:BusinessTwoMember]": "Description 2",
   *   "ssmt:DescriptionOfBusiness[ssmt:BusinessThreeMember]": ""
   * }
   *
   * @param backendData Backend data with nested business member objects
   * @returns Frontend format with bracket notation
   */
  transformBusinessMembersToFrontend(
    backendData: Record<string, any>
  ): Record<string, any> {
    const frontendData: Record<string, any> = {};

    // Member mapping: backend camelCase -> frontend PascalCase with prefix
    // Supports both camelCase (from localStorage) and PascalCase (from API) backend keys
    const memberMap: Record<string, string> = {
      businessOneMember: 'ssmt:BusinessOneMember',
      businessTwoMember: 'ssmt:BusinessTwoMember',
      businessThreeMember: 'ssmt:BusinessThreeMember',
      // PascalCase variants (as received from the API)
      BusinessOneMember: 'ssmt:BusinessOneMember',
      BusinessTwoMember: 'ssmt:BusinessTwoMember',
      BusinessThreeMember: 'ssmt:BusinessThreeMember',
    };

    // PHASE 1: Identify qnames that already have bracket notation keys in the input.
    // If bracket notation exists, it represents the latest user-edited values and should
    // take priority over stale nested objects from the original API data.
    const qnamesWithBracketNotation = new Set<string>();
    Object.keys(backendData).forEach((key) => {
      const bracketMatch = key.match(XBRL_PATTERNS.BUSINESS_MEMBER);
      if (bracketMatch) {
        // Check if this is a recognized business member bracket key
        const memberName = bracketMatch[2];
        const isBusinessMember = Object.values(memberMap).includes(memberName);
        if (isBusinessMember) {
          qnamesWithBracketNotation.add(bracketMatch[1]);
        }
      }
    });

    // PHASE 2: Transform data, skipping stale nested objects when bracket notation exists
    Object.entries(backendData).forEach(([qname, memberData]) => {
      // Check if this field has business member dimensions
      // Support both camelCase (from localStorage/state) and PascalCase (from API) keys
      if (
        memberData &&
        typeof memberData === 'object' &&
        !Array.isArray(memberData) &&
        (memberData.hasOwnProperty('businessOneMember') ||
          memberData.hasOwnProperty('businessTwoMember') ||
          memberData.hasOwnProperty('businessThreeMember') ||
          memberData.hasOwnProperty('BusinessOneMember') ||
          memberData.hasOwnProperty('BusinessTwoMember') ||
          memberData.hasOwnProperty('BusinessThreeMember'))
      ) {
        // If bracket notation already exists for this qname, skip the stale nested object.
        // The bracket notation values are the latest user-edited values and should win.
        if (qnamesWithBracketNotation.has(qname)) {
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`⏭️ Skipping stale nested business member object for "${qname}" (bracket notation values exist and take priority)`);
          return; // Skip - bracket notation keys will be copied as-is in the else branch
        }

        // Transform nested business member object to bracket notation
        Object.entries(memberMap).forEach(([backendMember, frontendMember]) => {
          if (memberData.hasOwnProperty(backendMember)) {
            const frontendKey = `${qname}[${frontendMember}]`;
            frontendData[frontendKey] = memberData[backendMember];
          }
        });
      } else {
        // Not a business member field - copy as-is
        frontendData[qname] = memberData;
      }
    });

    return frontendData;
  }

  /**
   * Transform frontend business member format to backend nested format
   * Reverses the transformation done by transformBusinessMembersToFrontend
   *
   * Frontend Format:
   * {
   *   "ssmt:MSICCode[ssmt:BusinessOneMember]": "70209",
   *   "ssmt:MSICCode[ssmt:BusinessTwoMember]": "64200",
   *   "ssmt:MSICCode[ssmt:BusinessThreeMember]": ""
   * }
   *
   * Backend Format (PascalCase nested object expected by C# BusinessMemberObject):
   * {
   *   "ssmt:MSICCode": {
   *     "BusinessOneMember": "70209",
   *     "BusinessTwoMember": "64200",
   *     "BusinessThreeMember": ""
   *   }
   * }
   *
   * @param frontendData Frontend data with bracket notation (and possibly stale nested objects)
   * @returns Backend format with PascalCase nested business member objects
   */
  transformBusinessMembersToBackend(
    frontendData: Record<string, any>
  ): Record<string, any> {
    const backendData: Record<string, any> = {};

    // Member mapping: frontend prefix notation -> backend PascalCase (matching C# BusinessMemberObject properties)
    const memberMap: Record<string, string> = {
      'ssmt:BusinessOneMember': 'BusinessOneMember',
      'ssmt:BusinessTwoMember': 'BusinessTwoMember',
      'ssmt:BusinessThreeMember': 'BusinessThreeMember',
    };

    // PHASE 1: Collect all qnames that have bracket notation keys
    const qnamesWithBracketNotation = new Set<string>();
    Object.keys(frontendData).forEach((key) => {
      const bracketMatch = key.match(XBRL_PATTERNS.BUSINESS_MEMBER);
      if (bracketMatch && memberMap[bracketMatch[2]]) {
        qnamesWithBracketNotation.add(bracketMatch[1]);
      }
    });

    // PHASE 2: Process all entries
    Object.entries(frontendData).forEach(([key, value]) => {
      const bracketMatch = key.match(XBRL_PATTERNS.BUSINESS_MEMBER);

      if (bracketMatch) {
        const qname = bracketMatch[1]; // e.g., "ssmt:MSICCode"
        const frontendMember = bracketMatch[2]; // e.g., "ssmt:BusinessOneMember"
        const backendMember = memberMap[frontendMember];

        if (backendMember) {
          // Initialize nested object if it doesn't exist or is not an object (e.g., stale JSON string)
          if (!backendData[qname] || typeof backendData[qname] !== 'object') {
            backendData[qname] = {};
          }
          // Set PascalCase key to match C# BusinessMemberObject property names
          backendData[qname][backendMember] = value;
        } else {
          backendData[key] = value;
        }
      } else {
        // Skip stale nested objects when bracket notation exists for the same qname
        if (this.isBusinessMemberNestedObject(value) && qnamesWithBracketNotation.has(key)) {
          return;
        }
        backendData[key] = value;
      }
    });

    return backendData;
  }

  /**
   * Check if a value is a nested business member object
   * Returns true for objects like { "BusinessOneMember": "01111", "businessTwoMember": "10622" }
   *
   * @param value - The value to check
   * @returns true if value is a nested business member object
   */
  private isBusinessMemberNestedObject(value: any): boolean {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return false;
    }

    const businessMemberKeys = [
      'businessOneMember', 'businessTwoMember', 'businessThreeMember',
      'BusinessOneMember', 'BusinessTwoMember', 'BusinessThreeMember',
    ];

    return businessMemberKeys.some(key => value.hasOwnProperty(key));
  }

  /**
   * Remove stale nested business member objects from sheet data.
   * When both nested format (e.g., "ssmt:MSICCode": { "BusinessOneMember": "01111" })
   * and bracket notation (e.g., "ssmt:MSICCode[ssmt:BusinessOneMember]": "47112") exist
   * in the same data object, the nested objects are stale API data that should be removed.
   *
   * This method returns a cleaned copy of the data with stale nested objects removed.
   * It does NOT modify the input object.
   *
   * @param data - Sheet data that may contain both formats
   * @returns Cleaned data with stale nested business member objects removed
   */
  removeStaleBusinessMemberObjects(data: Record<string, any>): Record<string, any> {
    if (!data || typeof data !== 'object') {
      return data;
    }

    // Identify qnames that have bracket notation keys
    const memberMap = [
      'ssmt:BusinessOneMember',
      'ssmt:BusinessTwoMember',
      'ssmt:BusinessThreeMember',
    ];

    const qnamesWithBracketNotation = new Set<string>();
    Object.keys(data).forEach((key) => {
      const bracketMatch = key.match(XBRL_PATTERNS.BUSINESS_MEMBER);
      if (bracketMatch) {
        const memberName = bracketMatch[2];
        if (memberMap.includes(memberName)) {
          qnamesWithBracketNotation.add(bracketMatch[1]);
        }
      }
    });

    // If no bracket notation keys found, nothing to clean up
    if (qnamesWithBracketNotation.size === 0) {
      return data;
    }

    // Create a cleaned copy, removing stale nested objects
    const cleanedData: Record<string, any> = {};
    Object.entries(data).forEach(([key, value]) => {
      if (this.isBusinessMemberNestedObject(value) && qnamesWithBracketNotation.has(key)) {
        // Skip stale nested object - bracket notation values are the current data
        
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`🧹 Removing stale nested business member object: "${key}" from state`);
      } else {
        cleanedData[key] = value;
      }
    });

    return cleanedData;
  }

  /**
   * Transform Statement of Financial Position data (Role 8)
   * Converts two-dimensional bracket notation to nested object with shortened keys
   *
   * Frontend format:
   *   "ifrs-full:Assets[Consolidated][CurrentYear]": 1000
   *   "ifrs-full:Assets[Separate][PreviousYear]": 2000
   *
   * Backend format:
   *   "ifrs-full:Assets": {
   *     "conCur": 1000,   // Consolidated + CurrentYear
   *     "sepPrev": 2000   // Separate + PreviousYear
   *   }
   */
  transformStatementDimensionDataToBackend(frontendData: Record<string, any>): Record<string, any> {
    const backendData: Record<string, any> = {};

    // Mapping for member types and periods (simplified format)
    const memberMap: Record<string, string> = {
      'Consolidated': 'con',
      'Separate': 'sep',
    };

    const periodMap: Record<string, string> = {
      'CurrentYear': 'Cur',
      'PreviousYear': 'Prev',
    };

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('🔄 Transforming Financial Position data to backend format...');

    Object.entries(frontendData).forEach(([key, value]) => {
      // Match pattern: qname[member][period]
      const match = key.match(XBRL_PATTERNS.DOUBLE_BRACKET);

      if (match) {
        const qname = match[1];           // e.g., "ifrs-full:Assets"
        const member = match[2];          // e.g., "Consolidated"
        const period = match[3];          // e.g., "CurrentYear"

        const memberKey = memberMap[member];
        const periodKey = periodMap[period];

        if (memberKey && periodKey) {
          // Create nested object if not exists
          if (!backendData[qname]) {
            backendData[qname] = {
              sepCur: null,
              sepPrev: null,
              conCur: null,
              conPrev: null,
            };
          }

          // Build composite key: sepCur, sepPrev, conCur, conPrev
          const compositeKey = memberKey + periodKey;
          backendData[qname][compositeKey] = value;

          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`  ✅ ${key} → ${qname}.${compositeKey} = ${value}`);
        } else {
          console.warn(`  ⚠️ Unknown member/period combination: ${member}/${period} - skipping`);
        }
      } else {
        // Not a two-dimensional bracket notation - skip (don't copy as-is)
        // This prevents dirty data like "MemberData: []" from being included
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`  ⏭️ ${key} → (skipped - no double bracket)`);
      }
    });

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('✅ Financial Position transformation complete');
    return backendData;
  }

  /**
   * Transform Simple Format Roles data (Roles 12-18) from frontend to backend
   * PHASE 2: Updated to use bracket notation parser
   *
   * OLD Frontend format:
   *   "ifrs-full:ProfitLossFromDiscontinuedOperationsConsolidatedCurrent": 1000
   *   "ifrs-full:ProfitLossFromDiscontinuedOperationsSeparatePrevious": 2000
   *
   * NEW Frontend format (PHASE 2):
   *   "ifrs-full:ProfitLossFromDiscontinuedOperations[Consolidated][CurrentYear]": 1000
   *   "ifrs-full:ProfitLossFromDiscontinuedOperations[Separate][PreviousYear]": 2000
   *
   * Backend format:
   *   "ifrs-full:ProfitLossFromDiscontinuedOperations": {
   *     "conCur": 1000,   // Consolidated Current
   *     "sepPrev": 2000   // Separate Previous
   *   }
   */
  transformStatementStringToBackend(frontendData: Record<string, any>): Record<string, any> {
    const backendData: Record<string, any> = {};

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('🔄 Transforming Simple Format Roles data to backend format (PHASE 2)...');

    Object.entries(frontendData).forEach(([key, value]) => {
      // PHASE 2: Parse bracket notation format
      // Expected format: qname[Consolidated|Separate][CurrentYear|PreviousYear]
      const bracketMatch = key.match(/^(.+?)\[(Consolidated|Separate)\]\[(CurrentYear|PreviousYear)\]$/);

      if (bracketMatch) {
        const qname = bracketMatch[1];
        const dimension = bracketMatch[2]; // "Consolidated" or "Separate"
        const period = bracketMatch[3];     // "CurrentYear" or "PreviousYear"

        // Map to backend composite key
        const dimensionPrefix = dimension === 'Consolidated' ? 'con' : 'sep';
        const periodSuffix = period === 'CurrentYear' ? 'Cur' : 'Prev';
        const compositeKey = dimensionPrefix + periodSuffix; // e.g., "conCur", "sepPrev"

        // Create nested object if not exists
        if (!backendData[qname]) {
          backendData[qname] = {
            sepCur: null,
            sepPrev: null,
            conCur: null,
            conPrev: null,
          };
        }

        // Set the value for the composite key
        backendData[qname][compositeKey] = value;

        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`  ✅ ${key} → ${qname}.${compositeKey} = ${value}`);
      } else {
        // If no bracket notation matched, skip (don't copy as-is)
        // This prevents dirty data like "MemberData: []" from being included
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`  ⏭️ ${key} → (skipped - not bracket format)`);
      }
    });

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('✅ Simple Format Roles transformation complete');
    return backendData;
  }

  /**
   * Transform Notes Issued Capital data (Role 23)
   * Converts double bracket notation to colon-based API format
   *
   * Frontend format (STANDARD BRACKET FORMAT):
   *   "ifrs-full:NumberOfSharesIssuedAndFullyPaid[Consolidated][ifrs-full:OrdinarySharesMember]": 15360000
   *   "ifrs-full:NumberOfSharesIssuedAndFullyPaid[Consolidated][Total]": 15367321
   *   "ifrs-full:NumberOfSharesIssuedAndFullyPaid[Separate][ifrs-full:OrdinarySharesMember]": 15360000
   *   "ifrs-full:NumberOfSharesIssuedAndFullyPaid[Separate][Total]": 15360000
   *
   * Backend format (with specific member):
   *   "ifrs-full:NumberOfSharesIssuedAndFullyPaid:OrdinarySharesMember": {
   *     "sepCur": "15360000",
   *     "sepPrev": null,
   *     "conCur": "15360000",
   *     "conPrev": null
   *   }
   *
   * Backend format (with Total - no member suffix):
   *   "ifrs-full:NumberOfSharesIssuedAndFullyPaid": {
   *     "sepCur": "15360000",
   *     "sepPrev": null,
   *     "conCur": "15367321",
   *     "conPrev": null
   *   }
   *
   * @param frontendData Object with double bracket notation
   * @returns Backend format with colon-separated keys and dimensional data values
   */
  transformQnameDimensionMemberToBackend(
    frontendData: Record<string, any>
  ): Record<string, any> {
    const backendData: Record<string, any> = {};

    // Mapping for dimension member to abbreviated form (simplified format)
    const dimensionMap: Record<string, string> = {
      'Consolidated': 'con',
      'Separate': 'sep',
    };

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('🔄 Transforming Notes Issued Capital data to backend format...');

    Object.entries(frontendData).forEach(([key, value]) => {
      // Match pattern: qname[dimension][member] or qname[dimension][Total]
      // Example: ifrs-full:NumberOfSharesIssuedAndFullyPaid[Consolidated][ifrs-full:OrdinarySharesMember]
      const match = key.match(XBRL_PATTERNS.DOUBLE_BRACKET);

      if (!match) {
        // Not in double bracket format
        // Include string, number, boolean, and null values (exclude arrays and objects)
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null) {
          backendData[key] = value;
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`  ✅ Simple value: ${key} → ${value}`);
        } else {
          // Skip arrays and complex objects like "MemberData: []"
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`  ⏭️ ${key} → (skipped - array or object)`);
        }
        return;
      }

      const qname = match[1];           // "ifrs-full:NumberOfSharesIssuedAndFullyPaid"
      const dimension = match[2];       // "Consolidated" or "Separate"
      const member = match[3];          // "ifrs-full:OrdinarySharesMember" or "Total"

      // Build API key
      let apiKey: string;

      if (member === 'Total') {
        // For [Total], use just the qname without member suffix
        apiKey = qname;
        
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`  ✅ Total: ${key} → ${apiKey}`);
      } else {
        // Extract member name without prefix (e.g., "OrdinarySharesMember")
        const memberParts = member.split(':');
        const memberName = memberParts.length === 2 ? memberParts[1] : member;

        // Regular format: qname:memberName
        apiKey = `${qname}:${memberName}`;
        
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`  ✅ Member: ${key} → ${apiKey}`);
      }

      // Initialize dimensional data object if it doesn't exist
      if (!backendData[apiKey]) {
        backendData[apiKey] = {
          sepCur: null,
          sepPrev: null,
          conCur: null,
          conPrev: null,
        };
      }

      // Map dimension to abbreviated form (con or sep)
      const dimensionKey = dimensionMap[dimension];

      if (dimensionKey) {
        // Assume all values are "Current" period (role23 doesn't have explicit period distinction in the frontend)
        const compositeKey = `${dimensionKey}Cur`;
        backendData[apiKey][compositeKey] = value;
        
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`  ✅ ${apiKey}.${compositeKey} = ${value}`);
      } else {
        console.warn(`  ⚠️ Unknown dimension: ${dimension}`);
      }
    });

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('✅ Notes Issued Capital transformation complete');
    return backendData;
  }

  // ===================================================================================================
  // ROLE TRANSFORMATION REGISTRY & UNIFIED TRANSFORMATION METHODS (Phase 1)
  // ===================================================================================================

  /**
   * Transformation registry mapping role numbers to their transformation functions
   * This registry provides a centralized lookup for how to transform each role's data
   *
   * Role Categories:
   * - No Transformation (Roles 0,1,3-7,20-22): Direct passthrough
   * - Business Member (Role 2): Special business member format
   * - Dimensional (Roles 8,9): Standard dimensional with brackets
   * - Simple Format (Roles 12,13,15,16,18): Standard dimensional with brackets
   * - Underscore Format (Role 19): Dimensional with underscores
   * - Double Bracket (Roles 23,24): Complex dimensional with member brackets
   */
  private readonly roleTransformationMap: Map<number, (apiData: any) => Record<string, any>> = new Map([
    // No Transformation Roles (passthrough)
    [0, (data) => this.transformNoTransformationRoles(0, data)],  // Filing Prerequisites
    [1, (data) => this.transformNoTransformationRoles(1, data)],  // Filing Info

    // Business Member Role
    [2, (data) => this.transformBusinessMemberRole(data)],  // Scope of Filing

    // No Transformation Roles (continued)
    [3, (data) => this.transformNoTransformationRoles(3, data)],  // Stock Exchange
    [4, (data) => this.transformNoTransformationRoles(4, data)],  // Directors Report
    [5, (data) => this.transformNoTransformationRoles(5, data)],  // Statement by Directors
    [6, (data) => this.transformNoTransformationRoles(6, data)],  // Director Business Review
    [7, (data) => this.transformNoTransformationRoles(7, data)],  // Auditors Report

    // Dimensional Roles (bracket format with Consolidated/Separate)
    [8, (data) => this.transformDimensionalRoles(8, data)],   // Statement of Financial Position
    [9, (data) => this.transformDimensionalRoles(9, data)],   // Sub-classification

    // Note: Roles 10, 11 are not implemented yet (liquidity variants)
    [10, (data) => this.transformDimensionalRoles(10, data)], // SOFP Liquidity (not implemented)
    [11, (data) => this.transformDimensionalRoles(11, data)], // Sub-classification Liquidity (not implemented)

    // Simple Format Roles (bracket format)
    [12, (data) => this.transformSimpleFormatRoles(12, data)], // Statement of Profit or Loss
    [13, (data) => this.transformSimpleFormatRoles(13, data)], // Analysis of Profit or Loss

    // Note: Roles 14, 15b are not implemented yet
    [14, (data) => this.transformSimpleFormatRoles(14, data)], // SOPL By Nature (not implemented)
    // Role 15b would be here but uses same number as Role 15

    // Simple Format Roles (continued)
    [15, (data) => this.transformSimpleFormatRoles(15, data)], // Statement of Comprehensive Income
    [16, (data) => this.transformSimpleFormatRoles(16, data)], // Comprehensive Income Before Tax

    // Note: Role 17 not implemented yet
    [17, (data) => this.transformSimpleFormatRoles(17, data)], // Cash Flows Direct (not implemented)

    [18, (data) => this.transformSimpleFormatRoles(18, data)], // Cash Flows Indirect

    // Underscore Format Role
    [19, (data) => this.transformUnderscoreFormatRole(data)],  // Statement of Changes in Equity

    // No Transformation Roles (Notes)
    [20, (data) => this.transformNoTransformationRoles(20, data)], // Notes Corporate Information
    [21, (data) => this.transformNoTransformationRoles(21, data)], // Notes Accounting Policies
    [22, (data) => this.transformNoTransformationRoles(22, data)], // Notes List of Notes

    // Double Bracket Roles (complex member dimensions)
    [23, (data) => this.transformDoubleBracketRoles(23, data)], // Notes Issued Capital
    [24, (data) => this.transformDoubleBracketRoles(24, data)], // Notes Related Party Transactions
  ]);

  /**
   * Master transformation method - routes role data to appropriate transformation function
   *
   * @param roleNumber Role number (0-24)
   * @param apiData Raw API data for the role
   * @returns Transformed data ready for form consumption
   *
   * @example
   * const transformed = this.transformRoleData(8, apiResponse['statement-of-financial-position']);
   */
  transformRoleData(roleNumber: number, apiData: any): Record<string, any> {
    // Handle null/undefined input
    if (!apiData) {
      console.warn(`⚠️ transformRoleData: No data provided for role ${roleNumber}`);
      return {};
    }

    // Get transformation function from registry
    const transformFn = this.roleTransformationMap.get(roleNumber);

    if (!transformFn) {
      console.error(`❌ transformRoleData: No transformation found for role ${roleNumber}`);
      console.warn(`⚠️ Returning data as-is (passthrough)`);
      return apiData;
    }

    try {
      const transformed = transformFn(apiData);
      
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log(`✅ Role ${roleNumber} transformed successfully`);
      
      return transformed;
    } catch (error) {
      console.error(`❌ Error transforming role ${roleNumber}:`, error);
      console.warn(`⚠️ Returning data as-is (passthrough)`);
      return apiData;
    }
  }

  // ===================================================================================================
  // CATEGORY-SPECIFIC TRANSFORMATION METHODS
  // ===================================================================================================

  /**
   * Transform roles with no transformation required (passthrough)
   *
   * Used by: Roles 0,1,3-7,20-22
   * - Role 0: Filing Prerequisites
   * - Role 1: Filing Info
   * - Role 3: Stock Exchange
   * - Role 4: Directors Report
   * - Role 5: Statement by Directors
   * - Role 6: Director Business Review
   * - Role 7: Auditors Report
   * - Role 20: Notes Corporate Information
   * - Role 21: Notes Accounting Policies
   * - Role 22: Notes List of Notes
   *
   * Input format: { qname: value } (simple key-value pairs)
   * Output format: { qname: value } (unchanged)
   *
   * @param roleNumber Role number for logging purposes
   * @param apiData API response data
   * @returns Data unchanged (passthrough)
   */
  private transformNoTransformationRoles(roleNumber: number, apiData: any): Record<string, any> {
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`📄 Role ${roleNumber}: No transformation required (passthrough)`);
    // Return data as-is - no transformation needed
    return apiData || {};
  }

  /**
   * Transform business member role data
   *
   * Used by: Role 2 (Scope of Filing)
   *
   * Input format: { qname: { businessOneMember, businessTwoMember, businessThreeMember } }
   * Output format: { qname[businessOneMember], qname[businessTwoMember], qname[businessThreeMember] }
   *
   * @param apiData API response data with business member structure
   * @returns Transformed data with bracketed member keys
   */
  private transformBusinessMemberRole(apiData: any): Record<string, any> {
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`🏢 Role 2: Transforming business member data`);

    if (!apiData || typeof apiData !== 'object') {
      return {};
    }

    // Use existing transformation method
    return this.transformBusinessMembersToFrontend(apiData);
  }

  /**
   * Transform dimensional roles (standard bracket format)
   *
   * Used by: Roles 8,9,10,11
   * - Role 8: Statement of Financial Position
   * - Role 9: Sub-classification
   * - Role 10: SOFP Liquidity (not implemented yet)
   * - Role 11: Sub-classification Liquidity (not implemented yet)
   *
   * Input format: { qname: { conCur, conPrev, sepCur, sepPrev } }
   * Output format: { qname[Consolidated][CurrentYear]: value, ... }
   *
   * @param roleNumber Role number for logging purposes
   * @param apiData API response data with dimensional structure
   * @returns Transformed data with composite bracket keys
   */
  private transformDimensionalRoles(roleNumber: number, apiData: any): Record<string, any> {
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`📊 Role ${roleNumber}: Transforming dimensional data (bracket format)`);

    if (!apiData || typeof apiData !== 'object') {
      return {};
    }

    const formData: Record<string, any> = {};

    // Transform each qname's dimensional data
    Object.entries(apiData).forEach(([qname, dimensionalData]) => {
      if (!dimensionalData || typeof dimensionalData !== 'object') {
        return;
      }

      // Use existing transformApiToFormFormat for each qname
      // Default to ifrs-full prefix (will be overridden by container if needed)
      const transformed = this.transformApiToFormFormat(qname, dimensionalData as Record<string, any>, 'ifrs-full');
      Object.assign(formData, transformed);
    });

    return formData;
  }

  /**
   * Transform simple format roles (bracket format without custom logic)
   *
   * Used by: Roles 12,13,14,15,15b,16,17,18
   * - Role 12: Statement of Profit or Loss
   * - Role 13: Analysis of Profit or Loss
   * - Role 14: SOPL By Nature (not implemented yet)
   * - Role 15b: Analysis By Nature (not implemented yet)
   * - Role 15: Statement of Comprehensive Income
   * - Role 16: Comprehensive Income Before Tax
   * - Role 17: Cash Flows Direct (not implemented yet)
   * - Role 18: Cash Flows Indirect
   *
   * Input format: { qname: { conCur, conPrev, sepCur, sepPrev } }
   * Output format: { qname[Consolidated][CurrentYear]: value, ... }
   *
   * @param roleNumber Role number for logging purposes
   * @param apiData API response data with dimensional structure
   * @returns Transformed data with composite bracket keys
   */
  private transformSimpleFormatRoles(roleNumber: number, apiData: any): Record<string, any> {
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`📋 Role ${roleNumber}: Transforming simple format data`);

    if (!apiData || typeof apiData !== 'object') {
      return {};
    }

    // Use existing transformation method
    return this.transformApiDataToSimpleFormat(apiData);
  }

  /**
   * Transform underscore format role (Statement of Changes in Equity)
   *
   * Used by: Role 19
   *
   * Input format: { qname:member: { ConCur, ConPrev, SepCur, SepPrev } }
   * Output format: { qname_member_Consolidated_Current: value, ... }
   *
   * @param apiData API response data with nested member structure
   * @returns Transformed data with underscore-separated keys
   */
  private transformUnderscoreFormatRole(apiData: any): Record<string, any> {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`🔄 Role 19: Transforming underscore format data (SOCE)`);

    if (!apiData || typeof apiData !== 'object') {
      return {};
    }

    // Use existing transformation method (the memberless default member is
    // derived per key from its taxonomy prefix).
    return this.transformApiDimensionalDataToUnderscoreFormat(apiData);
  }

  /**
   * Transform double bracket roles (complex member dimensions)
   *
   * Used by: Roles 23,24
   * - Role 23: Notes Issued Capital
   * - Role 24: Notes Related Party Transactions
   *
   * Input format: { qname: { member: { conCur, conPrev, sepCur, sepPrev } } }
   *                or { qname: { conCur, conPrev, sepCur, sepPrev } }
   * Output format: { qname[member][dimension][period]: value, ... }
   *
   * @param roleNumber Role number to determine which transformation to use
   * @param apiData API response data with nested member/dimensional structure
   * @returns Transformed data with double bracket keys
   */
  private transformDoubleBracketRoles(roleNumber: number, apiData: any): Record<string, any> {
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`🔢 Role ${roleNumber}: Transforming double bracket data`);

    if (!apiData || typeof apiData !== 'object') {
      return {};
    }

    // Role-specific transformations
    if (roleNumber === 23) {
      // Notes Issued Capital
      return this.transformNotesIssuedCapitalData(apiData);
    } else if (roleNumber === 24) {
      // Notes Related Party Transactions
      return this.transformNotesRelatedPartyTransactionsData(apiData);
    }

    // Fallback for unknown double bracket roles
    console.warn(`⚠️ Unknown double bracket role: ${roleNumber}`);
    return apiData;
  }

  // ===================================================================================================
  // ROLE 23 & 24 TRANSFORMATION HELPERS (Phase 2)
  // ===================================================================================================

  /**
   * Transform notes issued capital data from backend format to frontend form format (Role 23)
   *
   * Backend format:
   *   "{qname}": null                                              // TOTAL (display row, all empty)
   *   "{qname}:OrdinarySharesMember": { sepCur, conCur, ... }     // Share class row
   *
   * Frontend format (STANDARD BRACKET FORMAT):
   *   "{qname}[Consolidated][CurrentYear]": null                  // Total row keys with null
   *   "{qname}:member[Consolidated][CurrentYear]": value          // Share class data
   *
   * This follows the same pattern as other dimensional roles (8, 9, 12-18).
   *
   * @param backendData Backend API data
   * @returns Transformed data with standard bracket format keys
   */
  private transformNotesIssuedCapitalData(backendData: Record<string, any>): Record<string, any> {
    const transformedData: Record<string, any> = {};

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('🔄 Role 23 Transformation - Input data:', backendData);

    for (const [key, value] of Object.entries(backendData)) {
      // Pass-through any already-namespaced key (e.g. ifrs-full:, ifrs-smes:,
      // ssmt-mfrs:, ssmt-mpers:, ssmt-dei*:); only bare unprefixed keys get
      // the ifrs-full: default. Closes the doubled-prefix bug class
      const fullQname = ALREADY_NAMESPACED.test(key) ? key : `ifrs-full:${key}`;

      // Handle null values - create all 4 bracket format keys with null values
      if (value === null || value === undefined) {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`✅ Creating null Total row: ${fullQname}`);
        
        transformedData[`${fullQname}[Consolidated][CurrentYear]`] = null;
        transformedData[`${fullQname}[Consolidated][PreviousYear]`] = null;
        transformedData[`${fullQname}[Separate][CurrentYear]`] = null;
        transformedData[`${fullQname}[Separate][PreviousYear]`] = null;
        continue;
      }

      // Handle TextBlock fields (simple string values)
      if (typeof value === 'string' || !this.isShareClassDimensionalData(key, value)) {
        transformedData[fullQname] = value;
        
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`📝 TextBlock field: ${fullQname} = ${value}`);
        
        continue;
      }

      // Handle dimensional data (with or without member suffix) - STANDARD BRACKET FORMAT
      if (typeof value === 'object' && value !== null) {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`📊 Processing dimensional data: ${fullQname}`);

        // Parse values (use parseNumericValue which preserves null). Cells are
        // read case-insensitively, like every other dual-column role.
        const conCurValue = this.parseNumericValue(this.readDualColumnCell(value, 'conCur'));
        const conPrevValue = this.parseNumericValue(this.readDualColumnCell(value, 'conPrev'));
        const sepCurValue = this.parseNumericValue(this.readDualColumnCell(value, 'sepCur'));
        const sepPrevValue = this.parseNumericValue(this.readDualColumnCell(value, 'sepPrev'));

        // Only add non-null values to avoid storing explicit nulls
        if (conCurValue !== null) {
          transformedData[`${fullQname}[Consolidated][CurrentYear]`] = conCurValue;
        }
        if (conPrevValue !== null) {
          transformedData[`${fullQname}[Consolidated][PreviousYear]`] = conPrevValue;
        }
        if (sepCurValue !== null) {
          transformedData[`${fullQname}[Separate][CurrentYear]`] = sepCurValue;
        }
        if (sepPrevValue !== null) {
          transformedData[`${fullQname}[Separate][PreviousYear]`] = sepPrevValue;
        }

        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`   ✅ Transformed to bracket format keys`);
      }
    }

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('✅ Role 23 Transformation - Output data:', transformedData);
    return transformedData;
  }

  /**
   * Transform notes issued capital data from frontend form format to backend API format (Role 23)
   *
   * Frontend format (STANDARD BRACKET FORMAT):
   *   "{qname}[Consolidated][CurrentYear]": null                  // Total row keys
   *   "{qname}:member[Consolidated][CurrentYear]": value          // Share class data
   *
   * Backend format:
   *   "{qname}": null                                              // TOTAL (if all values are null)
   *   "{qname}:OrdinarySharesMember": { sepCur, conCur, ... }     // Share class row
   *
   * @param frontendData Frontend form data with bracket format keys
   * @returns Transformed data in backend API format
   */
  transformNotesIssuedCapitalToBackend(frontendData: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = {};

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('🔄 Role 23 Reverse Transformation - Input data:', frontendData);

    for (const [key, value] of Object.entries(frontendData)) {
      // Skip TextBlock fields (simple key-value pairs without brackets)
      if (!key.includes('[')) {
        result[key] = value;

        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`📝 TextBlock field: ${key} = ${value}`);
        
        continue;
      }

      // Parse bracket format key: qname:member[Consolidated][CurrentYear]
      const match = key.match(XBRL_PATTERNS.MEMBER_BRACKET_FORMAT);
      if (!match) {
        // Try simple bracket format (no member)
        const simpleMatch = key.match(XBRL_PATTERNS.BRACKET_FORMAT);
        if (simpleMatch) {
          const [, qname, dimension, period] = simpleMatch;
          const backendQname = qname;

          if (!result[backendQname] || typeof result[backendQname] !== 'object') {
            result[backendQname] = { conCur: null, conPrev: null, sepCur: null, sepPrev: null };
          }

          const backendKey = buildBackendKey(dimension as Dimension, period as Period);
          result[backendQname][backendKey] = value?.toString() || null;
        }
        continue;
      }

      const [, qname, member, dimension, period] = match;
      const backendQname = member ? `${qname}:${member}` : qname;

      // Initialize backend object with all 4 keys set to null
      if (!result[backendQname] || typeof result[backendQname] !== 'object') {
        result[backendQname] = { conCur: null, conPrev: null, sepCur: null, sepPrev: null };
      }

      // Build backend key (e.g., "conCur", "sepPrev")
      const backendKey = buildBackendKey(dimension as Dimension, period as Period);

      // Convert value to string or null (backend expects strings)
      result[backendQname][backendKey] = value?.toString() || null;
    }

    // Check for Total rows that are all null - convert to null
    for (const [qname, data] of Object.entries(result)) {
      if (typeof data === 'object' && data !== null) {
        const hasValue = Object.values(data).some(value => value !== null);
        if (!hasValue) {
          result[qname] = null;
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`✅ Converted all-null Total row to null: ${qname}`);
        }
      }
    }

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('✅ Role 23 Reverse Transformation - Output data:', result);
    
    return result;
  }

  /**
   * Check if a backend key-value pair represents dimensional share class data
   * Returns true if the value is an object with a sepCur/conCur cell, under any
   * casing (lowercase standard flow or PascalCase v2 direct export).
   */
  private isShareClassDimensionalData(key: string, value: any): boolean {
    if (typeof value !== 'object' || value === null) {
      return false;
    }

    return this.hasCurrentYearDualColumnCell(value);
  }

  /**
   * True when a cell object carries `sepCur` or `conCur` under any casing.
   * Shared by the role-23 and role-24 detectors so they cannot drift apart on
   * casing again (both used to accept lowercase keys only).
   */
  private hasCurrentYearDualColumnCell(value: Record<string, any>): boolean {
    return Object.keys(value).some((key) => {
      const lowered = key.toLowerCase();
      return lowered === 'sepcur' || lowered === 'concur';
    });
  }

  /**
   * Transform notes related party transactions data from backend format to frontend form format (Role 24)
   *
   * Backend format:
   *   "{qname}": { sepCur: "0", conCur: "0", sepPrev: "0", conPrev: "0" }  // TOTAL
   *   "{qname}:ParentMember": { sepCur: "0", conCur: "0", sepPrev: "0", conPrev: "0" }  // MEMBER VALUE
   *
   * Frontend format (STANDARD BRACKET FORMAT):
   *   "{qname}[Consolidated][CurrentYear]": value                      // Total row
   *   "{qname}:ParentMember[Consolidated][CurrentYear]": value         // Member data
   *
   * Note: Role 24 only uses CurrentYear data (conCur and sepCur). Previous year values are ignored.
   *
   * @param backendData Backend API data
   * @returns Transformed data with standard bracket format keys
   */
  private transformNotesRelatedPartyTransactionsData(backendData: Record<string, any>): Record<string, any> {
    const transformedData: Record<string, any> = {};

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('🔄 Role 24 Transformation - Input data:', backendData);

    for (const [key, value] of Object.entries(backendData)) {
      // See ALREADY_NAMESPACED note in transformNotesIssuedCapitalData —
      // same class of bug, same fix, kept symmetric for Role 24.
      const fullQname = ALREADY_NAMESPACED.test(key) ? key : `ifrs-full:${key}`;

      // Handle null values - create bracket format keys with null values
      if (value === null || value === undefined) {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`✅ Creating null row: ${fullQname}`);
        
        transformedData[`${fullQname}[Consolidated][CurrentYear]`] = null;
        transformedData[`${fullQname}[Separate][CurrentYear]`] = null;
        
        continue;
      }

      // Handle TextBlock fields (simple string values)
      if (typeof value === 'string' || !this.isRelatedPartyDimensionalData(key, value)) {
        transformedData[fullQname] = value;
        
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`📝 TextBlock field: ${fullQname} = ${value}`);
        
        continue;
      }

      // Handle dimensional data - STANDARD BRACKET FORMAT
      if (typeof value === 'object' && value !== null) {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`📊 Processing dimensional data: ${fullQname}`);

        // Parse values (only conCur and sepCur are used in Role 24), read
        // case-insensitively like every other dual-column role.
        const conCurValue = this.parseNumericValue(this.readDualColumnCell(value, 'conCur'));
        const sepCurValue = this.parseNumericValue(this.readDualColumnCell(value, 'sepCur'));

        // Only add non-null values to avoid storing explicit nulls
        if (conCurValue !== null) {
          transformedData[`${fullQname}[Consolidated][CurrentYear]`] = conCurValue;
        }
        if (sepCurValue !== null) {
          transformedData[`${fullQname}[Separate][CurrentYear]`] = sepCurValue;
        }

        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`   ✅ Transformed to bracket format keys`);
      }
    }

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('✅ Role 24 Transformation - Output data:', transformedData);
    
    return transformedData;
  }

  /**
   * Transform notes related party transactions data from frontend form format to backend API format (Role 24)
   *
   * Frontend format (STANDARD BRACKET FORMAT):
   *   "{qname}[Consolidated][CurrentYear]": value                      // Total row
   *   "{qname}:ParentMember[Consolidated][CurrentYear]": value         // Member data
   *
   * Backend format:
   *   "{qname}": { sepCur: "0", conCur: "0", sepPrev: "0", conPrev: "0" }  // TOTAL
   *   "{qname}:ParentMember": { sepCur: "0", conCur: "0", sepPrev: "0", conPrev: "0" }  // MEMBER
   *
   * Note: Role 24 only uses CurrentYear data. PreviousYear values are set to null.
   *
   * @param frontendData Frontend form data with bracket format keys
   * @returns Transformed data in backend API format
   */
  transformNotesRelatedPartyTransactionsToBackend(frontendData: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = {};

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('🔄 Role 24 Reverse Transformation - Input data:', frontendData);

    for (const [key, value] of Object.entries(frontendData)) {
      // Skip TextBlock fields (simple key-value pairs without brackets)
      if (!key.includes('[')) {
        result[key] = value;
        
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`📝 TextBlock field: ${key} = ${value}`);
        
        continue;
      }

      // Parse bracket format key: qname:member[Consolidated][CurrentYear]
      const match = key.match(XBRL_PATTERNS.MEMBER_BRACKET_FORMAT);
      if (!match) {
        // Try simple bracket format (no member)
        const simpleMatch = key.match(XBRL_PATTERNS.BRACKET_FORMAT);
        if (simpleMatch) {
          const [, qname, dimension, period] = simpleMatch;

          // Only process CurrentYear for Role 24
          if (period !== 'CurrentYear') {
            continue;
          }

          const backendQname = qname;

          if (!result[backendQname] || typeof result[backendQname] !== 'object') {
            result[backendQname] = { conCur: null, conPrev: null, sepCur: null, sepPrev: null };
          }

          const backendKey = buildBackendKey(dimension as Dimension, period as Period);
          result[backendQname][backendKey] = value?.toString() || null;
        }
        continue;
      }

      const [, qname, member, dimension, period] = match;

      // Only process CurrentYear for Role 24
      if (period !== 'CurrentYear') {
        continue;
      }

      const backendQname = member ? `${qname}:${member}` : qname;

      // Initialize backend object with all 4 keys set to null
      if (!result[backendQname] || typeof result[backendQname] !== 'object') {
        result[backendQname] = { conCur: null, conPrev: null, sepCur: null, sepPrev: null };
      }

      // Build backend key (e.g., "conCur", "sepCur")
      const backendKey = buildBackendKey(dimension as Dimension, period as Period);

      // Convert value to string or null (backend expects strings)
      result[backendQname][backendKey] = value?.toString() || null;
    }

    // Check for rows that are all null - convert to null
    for (const [qname, data] of Object.entries(result)) {
      if (typeof data === 'object' && data !== null) {
        const hasValue = Object.values(data).some(value => value !== null);
        if (!hasValue) {
          result[qname] = null;

          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`✅ Converted all-null row to null: ${qname}`);
        }
      }
    }

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('✅ Role 24 Reverse Transformation - Output data:', result);
    
    return result;
  }

  /**
   * Check if a backend key-value pair represents dimensional related party data
   * Returns true if the value is an object with a sepCur/conCur cell, under any
   * casing.
   */
  private isRelatedPartyDimensionalData(key: string, value: any): boolean {
    if (typeof value !== 'object' || value === null) {
      return false;
    }

    return this.hasCurrentYearDualColumnCell(value);
  }

}
